import { z } from 'zod';
import { NIVELES, ROLES } from '../permisos.ts';
import { decimalNoNegativo, email, uuid } from './comun.ts';

export const loginInput = z.object({
  email,
  password: z.string().min(1, 'La contraseña es obligatoria').max(200),
});
export type LoginInput = z.infer<typeof loginInput>;

export const rolSchema = z.enum(ROLES);

/** Usuario autenticado tal como lo ve el front (nunca incluye hash ni tokens). */
export const usuarioSesion = z.object({
  id: uuid,
  email,
  nombre: z.string(),
  rol: rolSchema,
  sucursal_id: uuid.nullable(),
  sucursal_nombre: z.string().nullable(),
  sucursal_codigo: z.string().nullable(),
  /** true cuando un superadmin opera una sucursal con contexto explícito (ADR-0003). */
  contexto_superadmin: z.boolean(),
  /** Permisos efectivos del usuario: plantilla de su rol + los que la dueña le dio o quitó (solo los que tiene). */
  permisos: z.record(z.string(), z.enum(NIVELES)),
  /** Sesión de caja abierta del cajero (null si no tiene). */
  sesion_caja: z
    .object({
      id: uuid,
      caja_id: uuid,
      caja_nombre: z.string(),
      abierta_en: z.string(),
      /** completa = facturas y notas · solo_fiscal = solo facturas. */
      modalidad: z.enum(['completa', 'solo_fiscal']).optional(),
      /** Ya declaró el conteo: la caja no vende más hasta terminar el cierre. */
      declarado: z.boolean().optional(),
    })
    .nullable(),
  /**
   * Caja que el usuario dejó abierta en OTRA sucursal (la dueña cambió de sucursal sin cerrarla). Aquí no se opera ni
   * se cierra: hay que volver a esa sucursal. Mientras siga abierta no puede abrir otra.
   */
  caja_en_otra_sucursal: z
    .object({ id: uuid, caja_nombre: z.string(), sucursal_id: uuid, sucursal_nombre: z.string(), abierta_en: z.string(), declarado: z.boolean() })
    .nullable()
    .optional(),
});
export type UsuarioSesion = z.infer<typeof usuarioSesion>;

export const loginRespuesta = z.object({
  token: z.string(),
  expira_en: z.number().int(),
  usuario: usuarioSesion,
});
export type LoginRespuesta = z.infer<typeof loginRespuesta>;

export const contextoSuperadminInput = z.object({
  sucursal_id: uuid,
  motivo: z.string().trim().min(3, 'Indica el motivo').max(300),
  /**
   * Recarga de página: vencimiento (epoch en segundos) del contexto que ya se tenía. El servidor devuelve el contexto
   * SOLO hasta ese momento; con esto nunca dura más de lo que duraba (solo puede acortar).
   */
  restaurar_hasta: z.number().int().positive().optional(),
});
export type ContextoSuperadminInput = z.infer<typeof contextoSuperadminInput>;

/**
 * La caja abre con su fondo fijo (el que quedó del turno anterior). El cajero lo confirma; si encontró otra cosa
 * escribe lo que hay y la dueña recibe el aviso.
 */
export const abrirCajaInput = z.object({
  caja_id: uuid,
  fondo_usd: decimalNoNegativo.optional(),
  fondo_bs: decimalNoNegativo.optional(),
});
export type AbrirCajaInput = z.infer<typeof abrirCajaInput>;

/** Cierre básico (Fase 0). El arqueo completo llega con la spec 11. */
export const cerrarCajaInput = z.object({
  observacion: z.string().trim().max(500).optional(),
});
export type CerrarCajaInput = z.infer<typeof cerrarCajaInput>;

export const usuarioCrearInput = z.object({
  email,
  nombre: z.string().trim().min(2).max(120),
  password: z.string().min(8, 'Mínimo 8 caracteres').max(200),
  rol: rolSchema,
  sucursal_id: uuid.nullable(),
});
export type UsuarioCrearInput = z.infer<typeof usuarioCrearInput>;

export const usuarioEditarInput = z.object({
  nombre: z.string().trim().min(2).max(120).optional(),
  rol: rolSchema.optional(),
  sucursal_id: uuid.nullable().optional(),
  activo: z.boolean().optional(),
  password: z.string().min(8).max(200).optional(),
});
export type UsuarioEditarInput = z.infer<typeof usuarioEditarInput>;
